import { useState, type ReactNode } from 'react'
import type { StoreApi, UseBoundStore } from 'zustand'
import { useAuthStore } from '../../stores/authStore'
import type { DashboardType, ManagedLayoutState } from '../../stores/layoutActions'
import { apiErrorMessage, findMyLayout, isBehindSystem, planSave, type SaveTarget } from '../../lib/dashboardLayouts'
import { ConfirmDialog } from '../ConfirmDialog'
import SaveLayoutDialog from './SaveLayoutDialog'
import LayoutUpdateBanner from './LayoutUpdateBanner'

type RevertAsk = { reason: 'revert' | 'adopt'; error: string | null }

/**
 * Wires a dashboard's layout store to the shared layout controls — the one-click 💾 Save, the Save
 * Layout dialog, Revert to system default, and the "system default was updated" banner — so all
 * four dashboards behave alike. Pages render `dialogs` and `banner` once, spread `menuProps` onto
 * LayoutPresetsMenu, and call the actions from their toolbar and mobile menu.
 */
export function useLayoutManager<S extends ManagedLayoutState>(
    useStore: UseBoundStore<StoreApi<S>>,
    dashboardType: DashboardType,
    opts: {
        /** The page's own admin half of the Save dialog; `close` shuts the dialog */
        renderAdmin?: (close: () => void) => ReactNode
        /** Runs as the Save dialog opens (pages reset their admin checkboxes here) */
        onOpenSaveDialog?: () => void
        /** Runs after a save made from the dialog (Inventory applies its admin options here) */
        afterDialogSave?: () => Promise<void>
    } = {},
) {
    const token = useAuthStore(s => s.accessToken)
    const userId = useAuthStore(s => s.user?.id ?? null)
    const layouts = useStore(s => s.availableLayouts)
    const currentLayoutId = useStore(s => s.currentLayoutId)
    const currentLayoutName = useStore(s => s.currentLayoutName)
    const isEditing = useStore(s => s.isEditing)
    const hasUnsavedChanges = useStore(s => s.hasUnsavedChanges())
    const systemUpdatedAt = useStore(s => s.systemLayoutUpdatedAt)
    const behindSystem = useStore(s => !s.isEditing && !!s.currentLayoutId && s.currentLayoutIsDefault
        && isBehindSystem(s.currentLayoutUpdatedAt, s.systemLayoutUpdatedAt))

    const [saveDialog, setSaveDialog] = useState<{ error: string | null } | null>(null)
    const [revertAsk, setRevertAsk] = useState<RevertAsk | null>(null)
    const [busy, setBusy] = useState(false)
    const [bannerError, setBannerError] = useState<string | null>(null)

    const store = () => useStore.getState()
    const signedIn = () => {
        if (!token || !userId) throw new Error('Your session has expired. Please sign in again.')
        return { token, userId }
    }

    const save = async (target: SaveTarget) => {
        await store().saveToAccount(signedIn().token, target)
        store().setEditing(false)
    }

    const showSaveDialog = (error: string | null) => {
        opts.onOpenSaveDialog?.()
        setSaveDialog({ error })
    }

    /** Save as…: the dialog, working from a freshly read list of the user's layouts. */
    const openSaveDialog = () => {
        showSaveDialog(null)
        if (token) store().refreshLayouts(token).catch(() => { /* the dialog works from the list it has */ })
    }

    /** 💾 Save: straight to the layout on screen, or to My layout — but replacing My layout asks first. */
    const quickSave = async () => {
        if (busy) return
        setBusy(true)
        try {
            // What Save writes to depends on what's saved, so decide from a fresh list.
            const fresh = await store().refreshLayouts(signedIn().token).catch(() => store().availableLayouts)
            const plan = planSave(fresh, store().currentLayoutId)
            if (plan.kind === 'replace-mine') showSaveDialog(null)
            else await save(plan.target)
        } catch (e) {
            showSaveDialog(apiErrorMessage(e))
        } finally {
            setBusy(false)
        }
    }

    const confirmRevert = async () => {
        setBusy(true)
        try {
            const { token, userId } = signedIn()
            await store().revertToSystemDefault(token, userId)
            setRevertAsk(null)
        } catch (e) {
            const error = apiErrorMessage(e, 'Your layout could not be reverted. Please try again.')
            setRevertAsk(ask => ask && { ...ask, error })
        } finally {
            setBusy(false)
        }
    }

    const keepMine = async () => {
        setBusy(true)
        setBannerError(null)
        try {
            await store().keepMyLayout(signedIn().token)
        } catch (e) {
            setBannerError(apiErrorMessage(e, 'That didn’t go through. Please try again.'))
        } finally {
            setBusy(false)
        }
    }

    const menuProps = {
        layouts, currentLayoutId, currentLayoutName, isEditing, hasUnsavedChanges, dashboardType,
        onOpen: () => { if (token) store().refreshLayouts(token).catch(() => { /* keep the list it has */ }) },
        onSelectLayout: (id: string) => { if (token && userId) store().loadLayout(token, userId, dashboardType, id) },
        onSaveFirst: quickSave,
        onRevert: () => setRevertAsk({ reason: 'revert', error: null }),
        onRename: (id: string, name: string) => store().renameLayout(signedIn().token, id, name),
        onDelete: (id: string) => {
            const { token, userId } = signedIn()
            return store().deleteLayout(token, userId, id)
        },
        onMakeDefault: (id: string) => store().makeDefaultLayout(signedIn().token, id),
    }

    const keepsNamed = layouts.some(l => !l.isDefault)
    const dialogs = (
        <>
            {saveDialog && (
                <SaveLayoutDialog
                    layouts={layouts}
                    currentLayoutId={currentLayoutId}
                    initialError={saveDialog.error}
                    onSave={async target => {
                        await save(target)
                        await opts.afterDialogSave?.()
                        setSaveDialog(null)
                    }}
                    onClose={() => setSaveDialog(null)}
                >
                    {opts.renderAdmin?.(() => setSaveDialog(null))}
                </SaveLayoutDialog>
            )}
            {revertAsk && (
                <ConfirmDialog
                    title={revertAsk.reason === 'adopt' ? 'Use the updated system default?' : 'Revert to the system default?'}
                    message={
                        <>
                            <p style={{ margin: 0 }}>
                                {revertAsk.reason === 'adopt'
                                    ? 'Your own layout for this dashboard is replaced by the system default, and it follows the system default from now on.'
                                    : 'Your own layout for this dashboard is removed, and it follows the system default again — including any later changes to it.'}
                                {keepsNamed && ' Your named layouts are kept.'}
                            </p>
                            {hasUnsavedChanges && <p style={{ margin: 'var(--space-2) 0 0' }}>Unsaved changes on screen are discarded.</p>}
                            {revertAsk.error && <div className="layout-ie-error" role="alert">{revertAsk.error}</div>}
                        </>
                    }
                    confirmLabel={busy ? 'Working…' : revertAsk.reason === 'adopt' ? 'Use it' : 'Revert'}
                    danger
                    confirmDisabled={busy}
                    onConfirm={confirmRevert}
                    onCancel={() => setRevertAsk(null)}
                />
            )}
        </>
    )

    const banner = behindSystem && systemUpdatedAt ? (
        <LayoutUpdateBanner
            updatedAt={systemUpdatedAt}
            busy={busy}
            error={bannerError}
            onUseIt={() => setRevertAsk({ reason: 'adopt', error: null })}
            onKeepMine={keepMine}
        />
    ) : null

    return {
        quickSave,
        openSaveDialog,
        requestRevert: () => setRevertAsk({ reason: 'revert', error: null }),
        /** Whether there is a My layout to revert */
        hasMyLayout: !!findMyLayout(layouts),
        busy,
        menuProps,
        dialogs,
        banner,
    }
}
